import { useEffect, useRef } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import styles from "./CustomCursor.module.css";

const DOT_SIZE = 6;
const RING_SIZE = 36;
const INTERACTIVE_SELECTOR = "a, button, [role='button'], [data-cursor-hover]";

export function CustomCursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice || reducedMotion) return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    dot.style.display = "block";
    ring.style.display = "block";
    document.documentElement.classList.add("custom-cursor-active");

    // Dot: fast tracking (near-instant)
    const dotX = gsap.quickTo(dot, "x", { duration: 0.08, ease: "power2.out" });
    const dotY = gsap.quickTo(dot, "y", { duration: 0.08, ease: "power2.out" });

    // Ring: slower tracking (trailing effect)
    const ringX = gsap.quickTo(ring, "x", { duration: 0.35, ease: "power3.out" });
    const ringY = gsap.quickTo(ring, "y", { duration: 0.35, ease: "power3.out" });

    const onMouseMove = (e: MouseEvent) => {
      dotX(e.clientX - DOT_SIZE / 2);
      dotY(e.clientY - DOT_SIZE / 2);
      ringX(e.clientX - RING_SIZE / 2);
      ringY(e.clientY - RING_SIZE / 2);
    };

    const onMouseEnterInteractive = () => {
      gsap.to(ring, {
        width: 56,
        height: 56,
        marginLeft: -10,
        marginTop: -10,
        borderColor: "rgba(255, 255, 255, 0.6)",
        duration: 0.35,
        ease: "power2.out",
      });
      gsap.to(dot, {
        scale: 0,
        duration: 0.2,
        ease: "power2.out",
      });
    };

    const onMouseLeaveInteractive = () => {
      gsap.to(ring, {
        width: RING_SIZE,
        height: RING_SIZE,
        marginLeft: 0,
        marginTop: 0,
        borderColor: "rgba(255, 255, 255, 0.4)",
        duration: 0.35,
        ease: "power2.out",
      });
      gsap.to(dot, {
        scale: 1,
        duration: 0.2,
        ease: "power2.out",
      });
    };

    window.addEventListener("mousemove", onMouseMove);

    // Delegate hover for interactive elements
    const observer = new MutationObserver(() => bindInteractiveElements());
    observer.observe(document.body, { childList: true, subtree: true });

    function bindInteractiveElements() {
      const elements = document.querySelectorAll(INTERACTIVE_SELECTOR);
      for (const el of elements) {
        if ((el as HTMLElement).dataset.cursorBound) continue;
        (el as HTMLElement).dataset.cursorBound = "1";
        el.addEventListener("mouseenter", onMouseEnterInteractive);
        el.addEventListener("mouseleave", onMouseLeaveInteractive);
      }
    }
    bindInteractiveElements();

    // Hide on mouse leave window
    const onMouseLeaveWindow = () => {
      gsap.to([dot, ring], { opacity: 0, duration: 0.2 });
    };
    const onMouseEnterWindow = () => {
      gsap.to([dot, ring], { opacity: 1, duration: 0.2 });
    };
    document.addEventListener("mouseleave", onMouseLeaveWindow);
    document.addEventListener("mouseenter", onMouseEnterWindow);

    // Mouse down/up feedback
    const onMouseDown = () => {
      gsap.to(ring, { scale: 0.8, duration: 0.15, ease: "power2.out" });
      gsap.to(dot, { scale: 1.5, duration: 0.15, ease: "power2.out" });
    };
    const onMouseUp = () => {
      gsap.to(ring, { scale: 1, duration: 0.3, ease: "elastic.out(1, 0.5)" });
      gsap.to(dot, { scale: 1, duration: 0.3, ease: "elastic.out(1, 0.5)" });
    };
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("mouseleave", onMouseLeaveWindow);
      document.removeEventListener("mouseenter", onMouseEnterWindow);
      document.documentElement.classList.remove("custom-cursor-active");
      observer.disconnect();
    };
  }, [reducedMotion]);

  return (
    <>
      <div ref={dotRef} className={styles.dot} style={{ display: "none" }} />
      <div ref={ringRef} className={styles.ring} style={{ display: "none" }} />
    </>
  );
}
